import { useState, useEffect, useContext } from 'react';
import { AccountContext } from '@/context/account.context';

/**
 * Wrap api calls in boilerplate for handling loading and error states.
 *
 * The api call is made once the user has an active session, and data is cleared out otherwise.
 *
 * @param apiCall - function that makes the api call, which returns a response with `data`
 * @param loadCondition - optional - boolean true will allow the api call
 */
export const useApi = (apiCall, loadCondition = null) => {
  const { hasActiveSession } = useContext(AccountContext);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [responseData, setResponseData] = useState(null);

  useEffect(() => {
    if (hasActiveSession && (loadCondition === null || loadCondition === true)) {
      loadApiData();
    } else {
      reset();
    }
  }, [hasActiveSession, loadCondition, loading, completed]);

  async function loadApiData(force = false) {
    try {
      if ((loading || completed) && force !== true) {
        // do not make the call again if loading is in process, or has already completed
        return;
      }
      setLoading(true);
      const resp = await apiCall();
      setResponseData(resp.data);
      setError(null);
    } catch (ex) {
      setResponseData(null);
      setError(ex);
    } finally {
      setLoading(false);
      setCompleted(true);
    }
  }

  // allow the caller to force a data reload
  function reload() {
    reset();
    loadApiData(true);
  }

  // queue a reset of the data and loading/error states
  function reset() {
    setResponseData(null);
    setCompleted(false);
    setLoading(false);
    setError(null);
  }

  return {
    error,
    loading,
    completed,
    responseData,
    reload
  };
};

export default useApi;
